import {
   List,
   ListItem,
   IconButton,
   ListItemButton,
   ListItemIcon,
   Checkbox,
   ListItemText,
   Grid,
   Divider,
} from '@mui/material';
import {Offset} from 'components/Offset';
import React from 'react';
import RolesMenu from './RolesMenu';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import {useIndeterminateCheckbox} from 'common/hooks/useIndeterminateCheckbox';
import {RoleNames} from 'types/diplomat/settings/RoleNames';
import {useFieldValue} from 'components/hook-form/useFieldValue';
import {IAdminRole} from 'types/diplomat/settings/adminroles/AdminRole';
import {IAdminRoles} from 'types/diplomat/settings/adminroles/AdminRoles';
import {some} from 'lodash';
import {useTranslation} from 'react-i18next';
import {IDiplomatObject} from 'types/DiplomatObject';
import {DiplomatObjectType} from 'types/DiplomatObjectType';

export type RoleListProps = {
   selected?: IDiplomatObject;
   select: (role: IDiplomatObject) => void;
   refetch: () => void;
   onDelete: (items: IDiplomatObject[]) => void;
   onCreate: () => void;
   hideItemsMenu?: boolean;
};

const RolesList = ({
   select,
   selected,
   refetch,
   onDelete,
   onCreate,
   hideItemsMenu,
}: RoleListProps) => {
   const {t} = useTranslation();
   const roles = useFieldValue<IAdminRoles>(RoleNames.roles) as IAdminRoles['roles'];

   const {
      isChecked,
      checked,
      handleCheck,
      toggleAll,
      indeterminate,
      checkedItems,
   } = useIndeterminateCheckbox<IAdminRole>(roles);

   const isAnyImmutable = React.useMemo(() => some(checkedItems, (item) => item.immutable), [
      checkedItems,
   ]);

   // console.debug('Roles', roles);
   // checked.indexOf(role) !== -1
   return (
      <>
         <Grid container spacing={3} justifyContent="space-between">
            <Grid item sm={1}>
               <Checkbox onClick={toggleAll} checked={checked} indeterminate={indeterminate} />
            </Grid>
            <Offset md={7} />
            <Grid item>
               <IconButton
                  disabled={isAnyImmutable || !checkedItems.length}
                  onClick={() => onDelete(checkedItems)}
                  title={t('Delete selected Admin Role(s)')}
               >
                  <DeleteOutlineIcon />
               </IconButton>
               <IconButton title={t('Add new Admin Role')} onClick={onCreate}>
                  <AddIcon />
               </IconButton>
            </Grid>
         </Grid>
         <List
            sx={{width: '100%', bgcolor: 'background.paper', maxHeight: 400, overflowY: 'scroll'}}
         >
            {roles.map((role, i) => {
               const labelId = `checkbox-list-label-${role.name}`;

               return (
                  <div key={role?.objectType + role?.id}>
                     <ListItem
                        secondaryAction={
                           hideItemsMenu ? undefined : (
                              <RolesMenu
                                 role={role}
                                 prefix={`${RoleNames.roles}.${i}`}
                                 refetch={refetch}
                                 onDelete={onDelete}
                              />
                           )
                        }
                        disablePadding
                     >
                        <ListItemButton
                           role={undefined}
                           onClick={() => select(role)}
                           dense
                           selected={
                              role.objectType === selected?.objectType && role.id === selected?.id
                           }
                        >
                           <ListItemIcon sx={{minWidth: 'auto', paddingRight: '1rem'}}>
                              <Checkbox
                                 key={role.name}
                                 edge="start"
                                 checked={isChecked(role)}
                                 tabIndex={-1}
                                 disableRipple
                                 inputProps={{'aria-labelledby': labelId}}
                                 onClick={handleCheck(role)}
                              />
                           </ListItemIcon>
                           <ListItemIcon sx={{minWidth: 'auto', paddingRight: '1rem'}}>
                              {role.objectType === DiplomatObjectType.enum.USER ? (
                                 <PersonIcon />
                              ) : (
                                 <GroupIcon />
                              )}
                           </ListItemIcon>
                           <ListItemText id={labelId} primary={role.name} />
                        </ListItemButton>
                     </ListItem>
                     <Divider />
                  </div>
               );
            })}
         </List>
      </>
   );
};

export default RolesList;
